import {
  CheckCheck,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Phone,
  Reply,
  Video,
} from 'lucide-react'
import { type ReactNode } from 'react'

import type {
  RenderedButton,
  RenderedTemplate,
  TemplateSlotLabel,
} from '@aeris/communication'
import { cn } from '@aeris/ui'

import { renderSpans } from './span-text.tsx'

/**
 * A pré-visualização 100% fiel do WhatsApp (F059).
 *
 * A doutrina é a de `receipt-dialog.tsx` (linhas 22-39): o documento é DADO
 * puro, produzido por um pacote (`renderTemplate`, `@aeris/communication`), e
 * este componente só o PERCORRE — nunca interpreta `structure`/`values` por
 * conta própria. Uma prévia "mais bonita" que discordasse do real seria um
 * SEGUNDO design, e a discordância só apareceria no celular do cliente. Por
 * isso a prop é `rendered: RenderedTemplate`, nunca `structure` + `values`.
 *
 * `dangerouslySetInnerHTML` é proibido pelo CLAUDE.md — o texto vem do
 * cliente (nome, número de pedido), então cada `WhatsAppSpan` é percorrido e
 * transformado em elementos React de verdade, nunca em uma string de HTML.
 *
 * Os botões são `div`, nunca `<button>`: um botão clicável numa prévia é uma
 * promessa falsa — nada acontece ao clicar — e um alvo de Tab que não faz
 * nada é pior que nenhum alvo.
 */

/** Cabeçalho de mídia (F066) — `TEXT` nunca chega aqui: ele desenha texto. */
type MediaHeader = Exclude<RenderedTemplate['headerFormat'], 'TEXT'>

const MEDIA_ICON: Record<MediaHeader, ReactNode> = {
  IMAGE: <ImageIcon aria-hidden="true" className="h-5 w-5" />,
  VIDEO: <Video aria-hidden="true" className="h-5 w-5" />,
  DOCUMENT: <FileText aria-hidden="true" className="h-5 w-5" />,
}

const MEDIA_LABEL: Record<MediaHeader, string> = {
  IMAGE: 'Imagem no cabeçalho',
  VIDEO: 'Vídeo no cabeçalho',
  DOCUMENT: 'Documento no cabeçalho',
}

const BUTTON_ICON: Record<RenderedButton['type'], ReactNode> = {
  QUICK_REPLY: <Reply aria-hidden="true" className="h-4 w-4" />,
  URL: <LinkIcon aria-hidden="true" className="h-4 w-4" />,
  PHONE_NUMBER: <Phone aria-hidden="true" className="h-4 w-4" />,
}

export type WhatsAppPreviewProps = {
  rendered: RenderedTemplate
  at: Date
  status?: 'sent' | 'delivered' | 'read'
  slotLabels?: readonly TemplateSlotLabel[]
  className?: string
}

/**
 * O painel de conversa em volta do balão — `bg-(--surface-sunken)`, a mesma
 * superfície do resto do produto para "fundo de conversa".
 */
export const WhatsAppPreview = ({
  rendered,
  at,
  status = 'read',
  slotLabels,
  className,
}: WhatsAppPreviewProps): ReactNode => {
  const tickColor =
    status === 'read' ? 'text-(--brand-primary)' : 'text-(--text-muted)'

  return (
    <figure
      className={cn(
        'flex flex-col gap-2 rounded-(--radius-lg) bg-(--surface-sunken) p-4',
        className
      )}
    >
      <figcaption className="sr-only">
        Prévia da mensagem como o cliente vai receber
      </figcaption>

      {/*
        O mesmo balão de saída da thread real — `atendimento.tsx:868-876`:
        `ml-auto` para alinhar à direita, `rounded-(--radius-lg)`,
        `bg-(--info-subtle)`, texto em `--text-primary`, `whitespace-pre-wrap`.
        Uma receita diferente aqui seria a mesma "segunda maquete" que a
        doutrina de `receipt-dialog.tsx` proíbe.
      */}
      <div className="ml-auto flex max-w-[85%] flex-col gap-1">
        <div className="rounded-(--radius-lg) bg-(--info-subtle) px-3 py-2 text-(length:--text-sm) whitespace-pre-wrap text-(--text-primary)">
          {rendered.headerFormat === 'TEXT' ? null : (
            // F066 — cabeçalho de mídia. Um retângulo com o ícone e o nome
            // do formato, não uma imagem de mentira: a prévia mostra o QUE
            // vai ali, e nunca finge conhecer o arquivo que ninguém subiu
            // ainda. O `aria-label` diz a mesma coisa em voz alta.
            <div
              role="img"
              aria-label={MEDIA_LABEL[rendered.headerFormat]}
              className="mb-2 flex h-24 items-center justify-center gap-2 rounded-(--radius-md) bg-(--surface-sunken) text-(length:--text-xs) text-(--text-muted)"
            >
              {MEDIA_ICON[rendered.headerFormat]}
              {MEDIA_LABEL[rendered.headerFormat]}
            </div>
          )}

          {rendered.header === null ? null : (
            <p className="mb-1 font-(--weight-semibold)">
              {renderSpans(rendered.header, slotLabels, 'header')}
            </p>
          )}

          <p>{renderSpans(rendered.body, slotLabels, 'body')}</p>

          {rendered.footer === null ? null : (
            <p className="mt-1 text-(length:--text-xs) text-(--text-muted)">
              {renderSpans(rendered.footer, slotLabels, 'footer')}
            </p>
          )}

          {rendered.buttons.length === 0 ? null : (
            <div className="mt-2 flex flex-col border-t border-(--border-default)">
              {rendered.buttons.map((button, index) => (
                // Um `div`, nunca `<button>` — ver o comentário no topo do
                // arquivo.
                <div
                  key={`${button.type}-${String(index)}`}
                  aria-hidden="true"
                  className="pointer-events-none flex items-center justify-center gap-2 py-2 text-(length:--text-sm) font-(--weight-medium) text-(--brand-primary)"
                >
                  {BUTTON_ICON[button.type]}
                  {button.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-1 text-(length:--text-xs) text-(--text-muted)">
          <span>
            {at.toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          <CheckCheck
            aria-hidden="true"
            className={cn('h-3.5 w-3.5', tickColor)}
          />
        </div>
      </div>
    </figure>
  )
}
